import styled from '@emotion/styled';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DonutSmallOutlined from '@mui/icons-material/DonutSmallOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExploreIcon from '@mui/icons-material/Explore';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import HiveOutlined from '@mui/icons-material/HiveOutlined';
// import HubIcon from '@mui/icons-material/Hub'; // Temporarily commented out with TestGen
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
// import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PolicyIcon from '@mui/icons-material/Policy';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import { Box } from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { ParagraphImageLeft } from '../base/icon/paragraph-image-left';
import useLeftNavContext from '../base/left-nav/left-nav-context';
import { CircularProgress } from '../base/progress';
import Text from '../base/text';
import { useOrganizationProjectContext } from '../organization-context';
import UserNavigationMenu from '../settings/user-navigation-menu';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import BetaOnly from '~/client/components/beta-only';
import { CapabilityAuth } from '~/client/components/capability-auth';
import FeatureAuth from '~/client/components/feature-auth';
import {
  POLICY_NAVIGATOR_URL,
  RUN_TEST_URL,
} from '~/client/config/assurance-urls';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
// import { useNotificationContext } from '~/client/contexts/notification-context';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import {
  LeftNavMenu,
  LeftNavMenuItem,
  LeftNavMenuGroup,
  LeftNavFlyoutMenuItem,
} from '~c/components/base/left-nav';
import {
  aiDashboardUtils,
  assuranceLibraryRoutesUtil,
  dashboardRouteUtil,
  governanceRoutesUtil,
  settingsRoutesUtil,
  vendorAgentAssessmentReportRouteUtil,
  vendorModelAssessmentReportRouteUtil,
} from '~c/routes';

interface Props {
  className?: string;
  tokens?: DesignTokenSet;
}

const DashboardLeftNavImpl: React.FC<Props> = ({ className, tokens }) => {
  const t = tokens ?? DesignToken;
  const DashboardLink = dashboardRouteUtil.Link;
  // Get unread count from context (shared across app)
  // const { unreadCount: unreadNotificationCount } = useNotificationContext();
  const { matchRoute, navigateTo } = dashboardRouteUtil.useHelper();
  const { navigateTo: navigateToAiSystem } = aiDashboardUtils.useHelper();
  const { navigateTo: navigateToSettings } = settingsRoutesUtil.useHelper();
  const { state, jumpTo } = useLeftNavContext();
  const { userProfile } = useUserProfile();
  const location = useLocation();

  // Accordion-style menu expansion - only one menu can be expanded at a time
  type ExpandedMenu = 'compliance' | 'guardian' | 'governance' | 'aiLab' | null;
  const [expandedMenu, setExpandedMenu] =
    React.useState<ExpandedMenu>('compliance');

  const handleMenuToggle = (menu: ExpandedMenu) => {
    setExpandedMenu((prev) => (prev === menu ? null : menu));
  };

  // Use centralized organization project context instead of separate query
  const { organizationProjectId, loading: orgProjectIdLoading } =
    useOrganizationProjectContext();

  // Fetch user's active organization contexts to verify membership
  const { data: contextData, loading: contextLoading } =
    GQL.useUserOrganizationContextsQuery({
      skip: !userProfile.emailVerified,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network', // Ensure fresh data after membership changes
    });

  // Check if user has active membership in the default organization
  const hasActiveMembership = React.useMemo(() => {
    if (
      !userProfile.defaultOrganizationId ||
      !contextData?.userOrganizationContexts
    ) {
      return false;
    }
    return contextData.userOrganizationContexts.some(
      (ctx) => ctx?.organization?._id === userProfile.defaultOrganizationId
    );
  }, [contextData, userProfile.defaultOrganizationId]);

  // Mutation to switch organization context
  const [switchContext] = GQL.useSwitchOrganizationContextMutation({
    onCompleted: (data_) => {
      if (data_.switchOrganizationContext.success) {
        // Redirect to compliance dashboard to update all organization-dependent data
        navigateTo('COMPLIANCE');
        window.location.reload();
      }
    },
  });

  // Track if auto-switch has been triggered to prevent multiple switches
  const [hasAutoSwitched, setHasAutoSwitched] = React.useState(false);

  // Auto-switch to first active organization if default org is deactivated
  React.useEffect(() => {
    if (hasAutoSwitched) {
      return;
    }

    if (
      !hasActiveMembership &&
      userProfile.defaultOrganizationId &&
      contextData?.userOrganizationContexts?.length &&
      contextData?.userOrganizationContexts?.length > 0
    ) {
      // User lost access to default org, automatically switch to first available org
      const firstActiveOrg =
        contextData?.userOrganizationContexts?.[0]?.organization?._id;
      if (
        firstActiveOrg &&
        firstActiveOrg !== userProfile.defaultOrganizationId
      ) {
        setHasAutoSwitched(true);
        switchContext({
          variables: { input: { organizationId: firstActiveOrg ?? '' } },
        });
      }
    }
  }, [
    hasActiveMembership,
    contextData,
    userProfile.defaultOrganizationId,
    switchContext,
    hasAutoSwitched,
  ]);

  const name = matchRoute('POLICY_LIBRARY')
    ? governanceRoutesUtil.routePrefix
    : matchRoute('ASSURANCE_LIBRARY')
      ? assuranceLibraryRoutesUtil.routePrefix
      : dashboardRouteUtil.routePrefix;

  // Auto-expand the appropriate menu based on current route
  React.useEffect(() => {
    const isVendorModelReport = location.pathname.match(
      /^\/vendor-model\/[^/]+\/assessment\/[^/]+\/report/
    );
    const isVendorAgentReport = location.pathname.match(
      /^\/vendor-agent\/[^/]+\/assessment\/[^/]+\/report/
    );

    if (
      matchRoute('POLICY_LIBRARY') ||
      matchRoute('ASSURANCE_LIBRARY') ||
      matchRoute('CONTROLGEN')
    ) {
      setExpandedMenu('governance');
    } else if (matchRoute('COMPLIANCE') || matchRoute('ORGANIZATION')) {
      setExpandedMenu('compliance');
    } else if (
      matchRoute('VENDOR_MODEL') ||
      matchRoute('VENDOR_AGENT') ||
      matchRoute('POLICY_GEN') ||
      isVendorModelReport ||
      isVendorAgentReport
    ) {
      setExpandedMenu('aiLab');
    }
  }, [matchRoute, location.pathname]);

  // Initialize left nav current route based on location (only when we're on a dashboard page)
  // Do not overwrite when on ai-system/project/assessment etc., so the landing left-nav
  // can show the correct menu (e.g. assessment back button).
  React.useEffect(() => {
    const isOnDashboard = location.pathname.startsWith(
      dashboardRouteUtil.routePrefix
    );
    if (isOnDashboard && state.currentRoute !== name) {
      jumpTo(name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isContextPending = contextLoading && !contextData;

  return (
    <LeftNavMenu
      className={className}
      name={name}
      header={{ showBackButton: false }}
    >
      {(!userProfile.emailVerified ||
        isContextPending ||
        orgProjectIdLoading) && (
        <Box
          color={t.nav.itemTextOnDark}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={2}
          height="100%"
        >
          <CircularProgress color="inherit" size={20} />
        </Box>
      )}
      {userProfile?.defaultOrganizationId &&
        !orgProjectIdLoading &&
        !isContextPending && (
          <>
            <LeftNavMenuGroup
              icon={VerifiedUserOutlined}
              title="Compliance"
              expandedIcon={ExpandLessIcon}
              collapsedIcon={ExpandMoreIcon}
              expanded={expandedMenu === 'compliance'}
              onToggle={() => handleMenuToggle('compliance')}
            >
              <CapabilityAuth capability="org:create_project">
                <LeftNavFlyoutMenuItem
                  component={DashboardLink}
                  to="COMPLIANCE"
                  icon={DashboardOutlinedIcon}
                  selected={matchRoute('COMPLIANCE')}
                >
                  Dashboard
                </LeftNavFlyoutMenuItem>
              </CapabilityAuth>
              {organizationProjectId ? (
                <LeftNavFlyoutMenuItem
                  icon={BusinessIcon}
                  selected={matchRoute('ORGANIZATION')}
                  onClick={() =>
                    navigateToAiSystem('DASHBOARD', {
                      params: { projectId: organizationProjectId },
                    })
                  }
                >
                  Organization
                </LeftNavFlyoutMenuItem>
              ) : (
                <LeftNavFlyoutMenuItem
                  component={DashboardLink}
                  to="ORGANIZATION"
                  icon={BusinessIcon}
                  selected={matchRoute('ORGANIZATION')}
                >
                  Organization
                </LeftNavFlyoutMenuItem>
              )}
              <LeftNavFlyoutMenuItem
                component={DashboardLink}
                to="PROJECTS"
                icon={AssignmentOutlinedIcon}
                selected={matchRoute('PROJECTS')}
              >
                Projects
              </LeftNavFlyoutMenuItem>
            </LeftNavMenuGroup>

            <CapabilityAuth capability="org:view_assurance">
              <LeftNavMenuGroup
                icon={GavelOutlined}
                title="Governance"
                expandedIcon={ExpandLessIcon}
                collapsedIcon={ExpandMoreIcon}
                expanded={expandedMenu === 'governance'}
                onToggle={() => handleMenuToggle('governance')}
              >
                <LeftNavFlyoutMenuItem
                  component={dashboardRouteUtil.Link}
                  to="POLICY_LIBRARY"
                  icon={LibraryBooksIcon}
                  selected={matchRoute('POLICY_LIBRARY')}
                >
                  Policy Library
                </LeftNavFlyoutMenuItem>
                <LeftNavFlyoutMenuItem
                  component={dashboardRouteUtil.Link}
                  to="ASSURANCE_LIBRARY"
                  icon={SecurityIcon}
                  selected={matchRoute('ASSURANCE_LIBRARY')}
                >
                  Assurance Library
                </LeftNavFlyoutMenuItem>
                {/*
                  Gated on the SAME capability the CONTROLGEN route enforces
                  (routes.tsx). CapabilityAuth renders null when denied, so a
                  reader who cannot open the page never sees the door. The old
                  AI Lab placement was behind org:view_assurance + requireBeta,
                  which is LOOSER than the route — this closes that gap rather
                  than carrying it across.
                */}
                <CapabilityAuth capability="platform:manage_system_admins">
                  <BetaOnly hidden>
                    <LeftNavFlyoutMenuItem
                      component={dashboardRouteUtil.Link}
                      to="CONTROLGEN"
                      icon={ParagraphImageLeft}
                      selected={matchRoute('CONTROLGEN')}
                    >
                      ControlGen <span className="beta-label">Beta</span>
                    </LeftNavFlyoutMenuItem>
                  </BetaOnly>
                </CapabilityAuth>
              </LeftNavMenuGroup>
            </CapabilityAuth>

            {/*
              Witness is its own group rather than a Compliance child: it is
              scoped to the ORGANIZATION and reads witness records directly, so
              it shows an agent that is emitting records before any project
              assessment exists. Nesting it under a project-shaped menu would
              imply a project scope it does not have.
            */}
            <CapabilityAuth capability="org:view_governance">
              <BetaOnly hidden>
                <LeftNavMenuGroup
                  icon={ReceiptLongOutlined}
                  title={
                    <Text sz="body14" noWrap align="left">
                      Witness <span className="beta-label">Beta</span>
                    </Text>
                  }
                  expandedIcon={ExpandLessIcon}
                  collapsedIcon={ExpandMoreIcon}
                  expanded={expandedMenu === 'guardian'}
                  onToggle={() => handleMenuToggle('guardian')}
                >
                  <LeftNavFlyoutMenuItem
                    component={DashboardLink}
                    to="GUARDIAN_COVERAGE"
                    icon={DonutSmallOutlined}
                    selected={matchRoute('GUARDIAN_COVERAGE')}
                  >
                    Coverage
                  </LeftNavFlyoutMenuItem>
                  <LeftNavFlyoutMenuItem
                    component={DashboardLink}
                    to="GUARDIAN_FLEET"
                    icon={HiveOutlined}
                    selected={
                      matchRoute('GUARDIAN_FLEET') ||
                      matchRoute('GUARDIAN_AGENTS') ||
                      matchRoute('GUARDIAN_AGENT_DETAIL')
                    }
                  >
                    Fleet
                  </LeftNavFlyoutMenuItem>
                </LeftNavMenuGroup>
              </BetaOnly>
            </CapabilityAuth>

            <CapabilityAuth capability="org:view_assurance">
              {/* TestGen temporarily commented out
              <LeftNavMenuItem
                icon={HubIcon}
                selected={matchRoute('TEST_GEN')}
                onClick={() => navigateTo('TEST_GEN')}
              >
                <Text sz="body14" noWrap align="left">
                  TestGen<sup className="trademark">TM</sup>{' '}
                  <span className="beta-label">Beta</span>
                </Text>
              </LeftNavMenuItem>
              */}
              <FeatureAuth requireBeta allowRedirect={false}>
                <LeftNavMenuGroup
                  icon={ScienceOutlined}
                  title={
                    <Text sz="body14" noWrap align="left">
                      AI Lab <span className="beta-label">Beta</span>
                    </Text>
                  }
                  flyoutTitle="AI Lab"
                  expandedIcon={ExpandLessIcon}
                  collapsedIcon={ExpandMoreIcon}
                  expanded={expandedMenu === 'aiLab'}
                  onToggle={() => handleMenuToggle('aiLab')}
                >
                  <LeftNavFlyoutMenuItem
                    component={dashboardRouteUtil.Link}
                    to="POLICY_GEN"
                    icon={PolicyIcon}
                    selected={matchRoute('POLICY_GEN')}
                  >
                    <Text sz="body14" noWrap align="left">
                      PolicyGen<sup className="trademark">TM</sup>
                    </Text>
                  </LeftNavFlyoutMenuItem>
                  <LeftNavFlyoutMenuItem
                    icon={ExploreIcon}
                    onClick={() => window.open(POLICY_NAVIGATOR_URL, '_blank')}
                    selected={false}
                  >
                    <Text sz="body14" noWrap align="left">
                      AI Policy Navigator
                    </Text>
                  </LeftNavFlyoutMenuItem>
                  <LeftNavFlyoutMenuItem
                    component={dashboardRouteUtil.Link}
                    to="VENDOR_MODEL"
                    icon={StorefrontIcon}
                    selected={
                      matchRoute('VENDOR_MODEL') ||
                      !!location.pathname.match(
                        /^\/vendor-model\/[^/]+\/assessment\/[^/]+\/report/
                      )
                    }
                  >
                    Vendor Models
                  </LeftNavFlyoutMenuItem>
                  <LeftNavFlyoutMenuItem
                    component={dashboardRouteUtil.Link}
                    to="VENDOR_AGENT"
                    icon={SmartToyIcon}
                    selected={
                      matchRoute('VENDOR_AGENT') ||
                      !!location.pathname.match(
                        /^\/vendor-agent\/[^/]+\/assessment\/[^/]+\/report/
                      )
                    }
                  >
                    Vendor Agents
                  </LeftNavFlyoutMenuItem>
                </LeftNavMenuGroup>
              </FeatureAuth>
            </CapabilityAuth>
            <CapabilityAuth capability="org:view_assurance">
              <LeftNavMenuItem
                icon={FactCheckOutlined}
                onClick={() => window.open(RUN_TEST_URL, '_blank')}
              >
                Verify
              </LeftNavMenuItem>
            </CapabilityAuth>
            <LeftNavMenuItem
              component={DashboardLink}
              to="TASKS"
              icon={AssignmentTurnedInOutlined}
              selected={matchRoute('TASKS')}
            >
              <div className="task-menu-item">Tasks</div>
            </LeftNavMenuItem>
            {/* <LeftNavMenuItem
              icon={NotificationsOutlinedIcon}
              selected={matchRoute('NOTIFICATIONS')}
              onClick={() => navigateTo('NOTIFICATIONS')}
            >
              <div className="task-menu-item">
                Notifications
                {unreadNotificationCount > 0 && (
                  <span className="notification-indicator" />
                )}
              </div>
            </LeftNavMenuItem> */}

            <UserNavigationMenu />
          </>
        )}
    </LeftNavMenu>
  );
};

const StyledDashboardLeftNav = styled(DashboardLeftNavImpl)<Props>`
  .grow {
    flex-grow: 1;
  }
  .userinfo {
    padding: 16px;
    text-transform: capitalize;
  }

  .task-menu-item {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .notification-indicator {
    width: 10px;
    height: 10px;
    background-color: ${(props) => (props.tokens ?? DesignToken).status.danger};
    border-radius: 50%;
    margin-left: 4px;
    display: inline-block;
    opacity: 0.7;

    /* Subtle animation for the glow */
    animation: pulse 2s ease-out infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.8);
      opacity: 0.7;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(0.8);
      opacity: 0.7;
    }
  }

  .v2-header {
    margin-left: -10px;
  }

  .trademark {
    font-size: smaller;
  }

  .beta-label {
    font-size: 9px;
    font-weight: 600;
    color: ${(props) => (props.tokens ?? DesignToken).status.warning};
    background-color: ${(props) =>
      (props.tokens ?? DesignToken).status.warningBg};
    padding: 2px 6px;
    border-radius: ${(props) => (props.tokens ?? DesignToken).radiusSmall}px;
    margin-left: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .submenu-container {
    padding-left: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
  }

  .submenu-item {
    .menu-item {
      height: 38px;
      padding: 8px 12px;
      padding-left: 40px;
      margin-bottom: 2px;
      font-size: 14px;

      .icon {
        font-size: 20px;
        margin-right: 10px;
      }

      .MuiTypography-root {
        font-size: 14px;
        font-weight: 400;
      }
    }
  }
  .menu-item {
    /* Button to take up width of container */
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: flex-start;

    height: 44px;
  }
`;

const DashboardLeftNav: React.FC<Props> = (props) => {
  const t = useDesignTokens();
  return <StyledDashboardLeftNav tokens={t} {...props} />;
};

export default DashboardLeftNav;
